import { Component, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AppStore, StoreRequest } from '@bn/shared/state-store';
import { ChipFilter, ChipFilterOption, FilterItemChips } from '@bn/web-ui/filter/models';
import { PaginationComponent } from '@bn/web-ui/pagination';
import { buildFilterOp, EVENT_LOG_DIMENSIONS, EventLogDimension, eventLogValueLabel } from '../../models';
import { EventLogsFilter } from '../../openapi';
import { AppShieldEventLogsSearchStore, ShieldApiStore } from '../../stores';
import { EventLogsGroupsTableComponent } from './components/event-logs-groups-table/event-logs-groups-table.component';
import { EventLogsTableComponent } from './components/event-logs-table/event-logs-table.component';
import { EventLogsDateRange, EventLogsToolbarComponent } from './components/event-logs-toolbar/event-logs-toolbar.component';

@Component({
  selector: 'app-app-shield-event-logs-search',
  templateUrl: './app-shield-event-logs-search.component.html',
  styleUrls: ['./app-shield-event-logs-search.component.scss'],
  imports: [EventLogsGroupsTableComponent, EventLogsTableComponent, EventLogsToolbarComponent, PaginationComponent],
})
export class AppShieldEventLogsSearchComponent {
  readonly store = inject(AppShieldEventLogsSearchStore);

  readonly #shieldApiStore = inject(ShieldApiStore);
  readonly #selectedShieldId = this.#shieldApiStore.selectedEntityId();

  readonly #listLoading = this.store.isLoading(StoreRequest.List);
  readonly isSearching = computed(() => this.#listLoading() || !this.store.hasSearched());
  readonly isExporting = this.store.isLoading(StoreRequest.Update);

  readonly #appStore = inject(AppStore);

  readonly #countryOptions = computed<ChipFilterOption[]>(() =>
    this.#appStore
      .countriesSelectList()
      .filter(({ Id }) => /^[A-Z]{2}$/.test(String(Id)))
      .map(({ Id, Name }) => ({ id: String(Id), label: Name })),
  );

  readonly chipsConfig = computed<FilterItemChips>(() => ({
    dimensions: EVENT_LOG_DIMENSIONS.filter((dimension) => dimension.filterable).map((dimension) => ({
      id: dimension.dimension,
      label: dimension.label,
      icon: dimension.icon,
      inputType: dimension.inputType,
      options: dimension.dimension === EventLogDimension.Country ? this.#countryOptions() : dimension.options,
      placeholder: dimension.placeholder,
      searchable: dimension.searchable,
    })),
    resolveOp: (dimensionId, value) => buildFilterOp(dimensionId as EventLogDimension, value),
    valueLabel: eventLogValueLabel,
  }));

  readonly chipsValue = computed<ChipFilter[]>(() => this.store.appliedFilters() as ChipFilter[]);

  constructor() {
    toObservable(this.#selectedShieldId)
      .pipe(takeUntilDestroyed())
      .subscribe((shieldId) => {
        if (shieldId) this.store.setShieldZoneId(Number(shieldId));
      });
  }

  onDateRangeChange({ from, to }: EventLogsDateRange): void {
    this.store.setDateRange(from, to);
  }

  onFiltersChange(chips: ChipFilter[]): void {
    this.store.setFilters((chips as EventLogsFilter[]) ?? []);
  }

  onPageChange(oneBasedPage: number): void {
    const zeroBasedPage = oneBasedPage - 1;

    if (zeroBasedPage === this.store.page()) return;

    this.store.setPage(zeroBasedPage);
  }
}
